import React, { useRef } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { MapRef } from 'react-map-gl';
import StreetViewThumb from './StreetViewThumb';
import { INTEL_MONO } from './IntelPopup';
import type { CameraInView } from './CamerasInViewStrip';
import { CAMERA_CATEGORY_COLORS, CAMERA_OVERLAY_COLOR, cameraDisplayLabel } from '../../services/cameraOverlayService';

/**
 * A camera pinned as a briefing-style callout: Street View still floating
 * near the camera with a dashed leader line back to the exact location. Offsets
 * and size are screen pixels relative to the camera point so the card keeps
 * its position while zooming - what analysts want when screenshotting for
 * slides.
 */
export type CameraCallout = {
  key: string;
  item: CameraInView;
  /** Card top-left relative to the camera point, in px. */
  dx: number;
  dy: number;
  /** Card width in px; height follows the image aspect + footer. */
  w?: number;
};

export const CALLOUT_CARD_W = 180;
export const CALLOUT_MIN_W = 120;
export const CALLOUT_MAX_W = 520;
/** Street View still aspect (w:h). */
const IMG_ASPECT = 180 / 96;
const FOOTER_H = 26;
/** Fixed request size for the still so resizing doesn't refetch imagery. */
const IMG_REQ_W = 320;
const IMG_REQ_H = Math.round(IMG_REQ_W / IMG_ASPECT);

export function calloutSize(w: number | undefined): { w: number; imgH: number; h: number } {
  const width = Math.min(CALLOUT_MAX_W, Math.max(CALLOUT_MIN_W, w ?? CALLOUT_CARD_W));
  const imgH = Math.round(width / IMG_ASPECT);
  return { w: width, imgH, h: imgH + FOOTER_H };
}

/** Stable identity for a camera: id plus location (ids may repeat across sources). */
export function cameraCalloutKey(item: CameraInView): string {
  return `${item.camera.camera_id}@${item.lon.toFixed(6)},${item.lat.toFixed(6)}`;
}

/** Default placement cycles through quadrants so successive pins don't stack. */
export function defaultCalloutOffset(index: number): { dx: number; dy: number } {
  const gap = 70;
  const { w, h } = calloutSize(undefined);
  const quadrants = [
    { dx: -w - gap, dy: -h - 20 }, // up-left
    { dx: gap, dy: -h - 20 }, // up-right
    { dx: -w - gap, dy: 30 }, // down-left
    { dx: gap, dy: 30 }, // down-right
  ];
  return quadrants[index % quadrants.length];
}

/**
 * Where the connector meets the card: the point on the card's border nearest
 * the camera (origin). Null when the camera sits under the card, where a line
 * would only clutter.
 */
function connectorEnd(dx: number, dy: number, w: number, h: number): { x: number; y: number } | null {
  const inside = dx <= 0 && 0 <= dx + w && dy <= 0 && 0 <= dy + h;
  if (inside) return null;
  return {
    x: Math.min(Math.max(0, dx), dx + w),
    y: Math.min(Math.max(0, dy), dy + h),
  };
}

type CalloutCardProps = {
  callout: CameraCallout;
  /** Camera point in map-container pixels. */
  x: number;
  y: number;
  previews: boolean;
  onOffsetChange: (key: string, dx: number, dy: number) => void;
  onResize: (key: string, w: number) => void;
  onRemove: (key: string) => void;
  onSelect?: (item: CameraInView) => void;
  opacity: number;
};

const CalloutCard: React.FC<CalloutCardProps> = ({
  callout,
  x,
  y,
  previews,
  onOffsetChange,
  onResize,
  onRemove,
  onSelect,
  opacity,
}) => {
  const { item, dx, dy, key } = callout;
  const { w, imgH, h } = calloutSize(callout.w);
  const label = cameraDisplayLabel(item.camera);
  const color = (item.camera.category && CAMERA_CATEGORY_COLORS[item.camera.category]) || CAMERA_OVERLAY_COLOR;
  const dragRef = useRef<{ startX: number; startY: number; dx: number; dy: number; moved: boolean } | null>(null);
  const resizeRef = useRef<{ startX: number; w: number } | null>(null);
  const movedRef = useRef(false);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, dx, dy, moved: false };
    movedRef.current = false;
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const mx = e.clientX - d.startX;
    const my = e.clientY - d.startY;
    if (!d.moved && Math.hypot(mx, my) < 4) return;
    d.moved = true;
    movedRef.current = true;
    onOffsetChange(key, d.dx + mx, d.dy + my);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
  };

  // Corner grip: width follows the pointer; height is derived from aspect.
  const onResizeDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    resizeRef.current = { startX: e.clientX, w };
  };
  const onResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = resizeRef.current;
    if (!r) return;
    onResize(key, r.w + (e.clientX - r.startX));
  };
  const onResizeUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    resizeRef.current = null;
  };

  return (
    <Box sx={{ position: 'absolute', left: x, top: y, width: 0, height: 0, pointerEvents: 'none' }}>
      <Box
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          left: dx,
          top: dy,
          width: w,
          height: h,
          pointerEvents: 'auto',
          cursor: 'move',
          touchAction: 'none',
          bgcolor: '#fff',
          color: '#111',
          border: '2px solid #fff',
          outline: '1px solid rgba(0,0,0,0.6)',
          borderRadius: '3px',
          boxShadow: '0 6px 18px rgba(0,0,0,0.45)',
          userSelect: 'none',
          opacity,
          '&:hover': { opacity: 1 },
          '&:hover .callout-close, &:hover .callout-resize': { opacity: 1 },
        }}
      >
        <Box sx={{ height: imgH, position: 'relative', overflow: 'hidden' }}>
          {previews ? (
            <StreetViewThumb
              lat={item.lat}
              lon={item.lon}
              heading={item.heading}
              enabled
              width={IMG_REQ_W}
              height={IMG_REQ_H}
              hideBadge
              onClick={() => {
                if (!movedRef.current) onSelect?.(item);
              }}
              sx={{ borderRadius: 0, border: 'none', height: '100%', aspectRatio: 'auto' }}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#e8e8e8',
                fontSize: '0.65rem',
                color: '#666',
              }}
            >
              No preview
            </Box>
          )}
          <Tooltip title="Remove callout" placement="top">
            <IconButton
              className="callout-close"
              size="small"
              aria-label="Remove callout"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(key);
              }}
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                p: '2px',
                opacity: 0,
                transition: 'opacity 120ms',
                bgcolor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 0.75, height: FOOTER_H }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          <Box
            sx={{
              fontFamily: label === item.camera.camera_id ? INTEL_MONO : undefined,
              fontSize: '0.68rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={label === item.camera.camera_id ? label : `${label} · ${item.camera.camera_id}`}
          >
            {label}
          </Box>
        </Box>
        {/* Resize grip (bottom-right). Diagonal hatch, shown on hover. */}
        <Box
          className="callout-resize"
          role="separator"
          aria-label="Resize callout"
          aria-orientation="horizontal"
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
          sx={{
            position: 'absolute',
            right: -1,
            bottom: -1,
            width: 14,
            height: 14,
            cursor: 'nwse-resize',
            touchAction: 'none',
            opacity: 0,
            transition: 'opacity 120ms',
            borderBottomRightRadius: '3px',
            background:
              'linear-gradient(135deg, transparent 0 50%, rgba(0,0,0,0.45) 50% 60%, transparent 60% 70%, rgba(0,0,0,0.45) 70% 80%, transparent 80% 90%, rgba(0,0,0,0.45) 90%)',
          }}
        />
      </Box>
    </Box>
  );
};

/** Badge radius used by the route-sequence layer; the ring anchor clears it. */
export const SEQUENCE_BADGE_RING_R = 11;

/**
 * Trim a leader so it starts `r` px away from the anchor (ring mode) instead
 * of at its centre.
 */
export function leaderStart(x: number, y: number, ex: number, ey: number, r: number): { x: number; y: number } | null {
  const dx = ex - x;
  const dy = ey - y;
  const len = Math.hypot(dx, dy);
  if (len <= r + 2) return null;
  return { x: x + (dx / len) * r, y: y + (dy / len) * r };
}

export type CameraCalloutsProps = {
  callouts: CameraCallout[];
  mapRef: React.RefObject<MapRef | null>;
  /** Street View previews allowed (admin embed + key). */
  previews: boolean;
  /**
   * 'dot' (default): filled anchor on the camera. 'ring': open ring that
   * leaves the route-sequence number underneath readable; the leader stops
   * at the ring.
   */
  anchor?: 'dot' | 'ring';
  onOffsetChange: (key: string, dx: number, dy: number) => void;
  onResize: (key: string, w: number) => void;
  onRemove: (key: string) => void;
  onSelect?: (item: CameraInView) => void;
  /** 0–1. Cards default to fully opaque; fade them to read the map underneath. */
  opacity?: number;
};

/**
 * Render as a SIBLING of <Map> inside the same relatively-positioned
 * container. Lives outside mapbox's canvas container so card clicks/drags
 * never reach the map's gesture or click handlers.
 */
const CameraCallouts: React.FC<CameraCalloutsProps> = ({
  callouts,
  mapRef,
  previews,
  anchor = 'dot',
  onOffsetChange,
  onResize,
  onRemove,
  onSelect,
  opacity = 1,
}) => {
  // Re-projected on every render (a handful of project() calls is cheap).
  // Not memoised on purpose: the parent re-renders on each map move/resize
  // (controlled viewState) and that is what keeps the cards attached.
  const map = mapRef.current?.getMap?.();
  const positioned = map
    ? callouts.map((c) => {
        const p = map.project([c.item.lon, c.item.lat]);
        return { callout: c, x: p.x, y: p.y };
      })
    : [];

  if (positioned.length === 0) return null;
  return (
    <Box
      sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 2 }}
      data-camera-callouts=""
    >
      {/* Leaders + anchor dots for every callout in one container-sized svg.
          Recomputed from the live offset/size each render, so they follow
          card drags, resizes and map pans. */}
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', overflow: 'visible' }}
        aria-hidden
      >
        {positioned.map(({ callout, x, y }) => {
          const { w, h } = calloutSize(callout.w);
          const end = connectorEnd(callout.dx, callout.dy, w, h);
          const color =
            (callout.item.camera.category && CAMERA_CATEGORY_COLORS[callout.item.camera.category]) || CAMERA_OVERLAY_COLOR;
          const ring = anchor === 'ring';
          const start = end ? (ring ? leaderStart(x, y, x + end.x, y + end.y, SEQUENCE_BADGE_RING_R) : { x, y }) : null;
          return (
            <g key={callout.key}>
              {end && start && (
                <>
                  {/* Dark halo underneath keeps the dashes legible over light imagery and roads. */}
                  <line x1={start.x} y1={start.y} x2={x + end.x} y2={y + end.y} stroke="rgba(0,0,0,0.7)" strokeWidth={3.5} strokeLinecap="round" />
                  <line
                    x1={start.x}
                    y1={start.y}
                    x2={x + end.x}
                    y2={y + end.y}
                    stroke="#fff"
                    strokeWidth={1.5}
                    strokeDasharray="6 4"
                    strokeLinecap="round"
                  />
                  <circle cx={x + end.x} cy={y + end.y} r={2.5} fill="#fff" stroke="rgba(0,0,0,0.7)" strokeWidth={1} />
                </>
              )}
              {ring ? (
                // Open ring around the sequence badge so its number stays readable.
                <circle cx={x} cy={y} r={SEQUENCE_BADGE_RING_R} fill="none" stroke="#fff" strokeWidth={1.5} style={{ filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.8))' }} />
              ) : (
                <circle cx={x} cy={y} r={4.5} fill={color} stroke="#fff" strokeWidth={1.5} />
              )}
            </g>
          );
        })}
      </svg>
      {positioned.map(({ callout, x, y }) => (
        <CalloutCard
          key={callout.key}
          callout={callout}
          x={x}
          y={y}
          previews={previews}
          onOffsetChange={onOffsetChange}
          onResize={onResize}
          onRemove={onRemove}
          onSelect={onSelect}
          opacity={opacity}
        />
      ))}
    </Box>
  );
};

export default CameraCallouts;
